import SelectLocation from '@/basicsComponents/Selects/SelectLocation';
import type { InpFile } from '@/domain/ModelFile/Inp';
import InpService from '@/domain/ModelFile/InpService';
import useDebounce from '@/hooks/useDebounce';
import useInpFiles from '@/hooks/useInpFiles';
import { Button, Search, DataGrid, Icon } from '@hydrodigital/design-system';
import { Tooltip, notification } from 'antd';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import dayjs from 'dayjs';
import { FC, useEffect, useRef, useState } from 'react';
import ControlKPIModal from './ControlKPIModal';

interface Module {
	name: string;
	id: number;
}

interface SecurityData {
	firstname: string;
	lastname: string;
	timeZone: string;
	email: string;
	role: string;
	roleId: string;
	modules: Module[];
	active: boolean;
	organizationId: number;
	organizationName: string;
	phoneNumber: string;
	address: string | null;
	zipCode: string | null;
	hasAcceptedEula: boolean;
	requiresEulaAcceptance: boolean;
	eulaVersion: number;
}

const inpService = new InpService();

const ControlKPIsTab: FC = () => {
	const userId = localStorage.getItem('userId') ?? '';
	const security = localStorage.getItem('security') ?? '{}';
	const securityData = JSON.parse(security) as SecurityData;
	const organizationId = securityData.organizationId;

	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [name, setName] = useState<string>('');
	const [locationId, setLocationId] = useState<number | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
	const [editingInpId, setEditingInpId] = useState<number | undefined>(undefined);
	const [editingLocationId, setEditingLocationId] = useState<number | undefined>(undefined);
	const [editingRecord, setEditingRecord] = useState<InpFile | undefined>(undefined);
	const [deletingInpId, setDeletingInpId] = useState<number | null>(null);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [selectedRecord, setSelectedRecord] = useState<InpFile | null>(null);
	const selectLocationRef = useRef<{ setValue: (value: string) => void }>(null);

	const debouncedName = useDebounce(name, 500);

	const [refreshKey, setRefreshKey] = useState(0);

	const { inpFiles, pagination } = useInpFiles({
		organizationId,
		page: paginationState.pageIndex + 1,
		size: paginationState.pageSize,
		hasControlKpi: true,
		name: debouncedName || null,
		locationId: locationId,
		refreshTrigger: refreshKey,
	});

	useEffect(() => {
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, [debouncedName, locationId]);

	const handleModalClose = () => {
		setIsModalOpen(false);
		setModalMode('create');
		setEditingInpId(undefined);
		setEditingLocationId(undefined);
		setEditingRecord(undefined);
	};

	const handleModalSuccess = () => {
		setRefreshKey((prev) => prev + 1);
	};

	const handleCreateKPI = () => {
		setModalMode('create');
		setEditingInpId(undefined);
		setEditingLocationId(undefined);
		setEditingRecord(undefined);
		setIsModalOpen(true);
	};

	const handleEditKPI = (record: InpFile) => {
		setModalMode('edit');
		setEditingInpId(Number(record.inpId));
		setEditingLocationId(Number(record.locationId));
		setEditingRecord(record);
		setIsModalOpen(true);
	};

	const handleDeleteKPI = (record: InpFile) => {
		setSelectedRecord(record);
		setShowConfirmDelete(true);
	};

	const executeDelete = async () => {
		if (!selectedRecord) return;

		setDeletingInpId(Number(selectedRecord.inpId));
		try {
			const response = await inpService.deleteControlKpi(Number(selectedRecord.inpId));

			if (response.success) {
				notification.success({
					message: 'Successful',
					description: response.message || 'Recommendation Logic deleted successfully',
				});

				setRefreshKey((prev) => prev + 1);
			} else {
				notification.error({
					message: 'Error',
					description: response.message || 'Failed to delete Recommendation Logic',
				});
			}
		} catch (error) {
			console.error('Error deleting Recommendation Logic:', error);
			notification.error({
				message: 'Error',
				description: 'Failed to delete Recommendation Logic',
			});
		} finally {
			setDeletingInpId(null);
			setShowConfirmDelete(false);
			setSelectedRecord(null);
		}
	};

	const columns = [
		{
			accessorKey: 'locationName',
			header: 'Location',
			cell: (cellContext: any) => (
				<span style={{ color: '#232B36', fontSize: '14px', fontWeight: '500' }}>
					{cellContext.getValue()}
				</span>
			),
		},
		{
			accessorKey: 'inpName',
			header: 'Inp Files',
			cell: (cellContext: any) => (
				<span style={{ color: '#232B36', fontSize: '14px' }}>{cellContext.getValue()}</span>
			),
		},
		{
			accessorKey: 'controlKpiCreatedBy',
			header: 'Added by',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as InpFile;
				return (
					<div className="flex column">
						<span
							style={{
								color: '#232B36',
								fontSize: '14px',
								fontWeight: '500',
							}}
						>
							{row.controlKpiCreatedBy ?? ''}
						</span>
						<span
							style={{
								color: '#74788D',
								fontSize: '12px',
							}}
						>
							{row.createdAt && dayjs(row.createdAt).format('YYYY/MM/DD [at] hh:mm a')}
						</span>
					</div>
				);
			},
		},
		{
			accessorKey: 'actions',
			header: 'Actions',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as InpFile;
				return (
					<div className="flex" style={{ gap: '12px' }}>
						<Tooltip title="Edit">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => {
									handleEditKPI(row);
								}}
							>
								<Icon name="edit-alt" size="m" hoverable />
							</span>
						</Tooltip>
						<Tooltip title="Delete">
							<span
								style={{
									cursor: deletingInpId === Number(row.inpId) ? 'not-allowed' : 'pointer',
									display: 'inline-flex',
									opacity: deletingInpId === Number(row.inpId) ? 0.5 : 1,
								}}
								onClick={() => {
									if (deletingInpId !== Number(row.inpId)) {
										handleDeleteKPI(row);
									}
								}}
							>
								<Icon name="trash-alt" size="m" hoverable />
							</span>
						</Tooltip>
					</div>
				);
			},
		},
	];

	return (
		<div className="flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>
			<div className="flex justify-between" style={{ flexShrink: 0 }}>
				<div className="flex">
					<div style={{ width: 200, minWidth: 200 }}>
						<Search
							placeholder="Search"
							size="small"
							value={name}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setName(e.target.value);
							}}
							showClearButton={true}
							onClear={() => {
								setName('');
							}}
							style={{ width: '100%' }}
						/>
					</div>
					<div style={{ marginLeft: 30 }}>
						<SelectLocation
							ref={selectLocationRef}
							userId={userId}
							onSelect={(value: string | number | null) => {
								setLocationId(value ? Number(value) : null);
							}}
						/>
					</div>
				</div>
				<div>
					<Button
						label="Add Recommendation Logic"
						size="small"
						variant="secondary"
						iconName="plus-circle"
						onClick={handleCreateKPI}
					/>
				</div>
			</div>

			<DataGrid
				columns={columns}
				data={inpFiles || []}
				pagination={{
					enabled: true,
					manualPagination: true,
					rowCount: pagination.totalRows,
					defaultPageSize: 10,
					pageSizeOptions: [10, 20, 50],
				}}
				className='mt-3'
				stickyHeader={true}
				maxHeight="calc(100vh - 300px)"
				paginationState={paginationState}
				onPaginationChange={setPaginationState}
			/>

			<ControlKPIModal
				isOpen={isModalOpen}
				onClose={handleModalClose}
				userId={userId}
				organizationId={organizationId}
				onSuccess={handleModalSuccess}
				mode={modalMode}
				inpId={editingInpId}
				locationId={editingLocationId}
				locationName={editingRecord?.locationName}
				inpName={editingRecord?.inpName}
			/>

			<ConfirmationModal
				title="Delete Recommendation Logic?"
				message={`Are you sure you want to delete the Recommendation Logic for "${selectedRecord?.inpName}"? This action cannot be undone.`}
				showModal={showConfirmDelete}
				onAccept={executeDelete}
				oncancel={() => {
					setShowConfirmDelete(false);
					setSelectedRecord(null);
				}}
			/>
		</div>
	);
};

export default ControlKPIsTab;
